import { useState, useEffect } from 'react'
import api from '../../services/apiService'

/**
 * The people this city has appointed to score its matches.
 *
 * A scorer covers one sport in this coordinator's city. Credentials are shown
 * once, at creation, because the password is hashed the moment it is stored.
 */
const MyScorers = () => {
  const [scorers, setScorers] = useState([])
  const [sports, setSports] = useState([])
  const [city, setCity] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null)
  const [issued, setIssued] = useState(null)      // credentials shown once
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ full_name: '', sport: '', phone: '' })

  useEffect(() => { load() }, [])

  const load = async () => {
    setLoading(true)
    try {
      const res = await api.get('/user/scorers')
      setScorers(res.data?.data || [])
      setSports(res.data?.sports || [])
      setCity(res.data?.city || '')
    } catch (err) {
      console.error('Error loading scorers:', err)
      setMessage({ tone: 'error', text: 'Could not load your scorers.' })
    } finally {
      setLoading(false)
    }
  }

  const create = async () => {
    if (!form.full_name.trim() || !form.sport) return
    setBusy(true)
    setMessage(null)
    try {
      const res = await api.post('/user/scorers', form)
      setIssued({ ...res.data.credentials, name: form.full_name })
      setForm({ full_name: '', sport: '', phone: '' })
      setAdding(false)
      await load()
    } catch (err) {
      setMessage({ tone: 'error', text: err.response?.data?.message || 'Could not create that scorer.' })
    } finally {
      setBusy(false)
    }
  }

  const act = async (id, fn, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return
    setBusy(true)
    try {
      const res = await fn()
      setMessage({ tone: 'ok', text: res.data.message })
      await load()
    } catch (err) {
      setMessage({ tone: 'error', text: err.response?.data?.message || 'That did not work.' })
    } finally {
      setBusy(false)
    }
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value })

  if (loading) return <div style={wrap}><p>Loading scorers…</p></div>

  return (
    <div style={wrap}>
      <div style={head}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26 }}>Match Scorers</h1>
          <p style={{ margin: '4px 0 0', color: '#5c6b7f' }}>
            People who record results for {city || 'your city'}. Each covers one sport.
          </p>
        </div>
        <button onClick={() => { setAdding(!adding); setIssued(null) }} style={primaryBtn}>
          {adding ? 'Cancel' : 'Add scorer'}
        </button>
      </div>

      {message && <div style={banner(message.tone)}>{message.text}</div>}

      {issued && (
        <div style={banner('ok')}>
          <strong>{issued.name} can now sign in.</strong> Pass these on — the password
          is not shown again.
          <div style={credBox}>
            <span>Username <b>{issued.username}</b></span>
            <span>Password <b>{issued.password}</b></span>
          </div>
        </div>
      )}

      {adding && (
        <div style={addBox}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 10 }}>
            <label style={lbl}>Name
              <input value={form.full_name} onChange={set('full_name')} placeholder="Who is scoring" style={inp} />
            </label>
            <label style={lbl}>Sport
              <select value={form.sport} onChange={set('sport')} style={inp}>
                <option value="">Choose a sport…</option>
                {sports.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </label>
            <label style={lbl}>Phone (optional)
              <input value={form.phone} onChange={set('phone')} placeholder="+91…" style={inp} />
            </label>
          </div>
          <p style={{ color: '#5c6b7f', fontSize: 12.5, margin: '10px 0 0' }}>
            They will be able to score {form.sport ? sports.find((s) => s.key === form.sport)?.label : 'that sport'} matches
            in {city} only. A username and password are generated for you.
          </p>
          <button onClick={create} disabled={busy || !form.full_name.trim() || !form.sport}
                  style={{ ...primaryBtn, marginTop: 12 }}>
            {busy ? 'Creating…' : 'Create scorer'}
          </button>
        </div>
      )}

      {scorers.length === 0 ? (
        <div style={empty}>
          <strong>No scorers yet.</strong>
          <p style={{ margin: '6px 0 0', color: '#5c6b7f' }}>
            Add someone for each sport you are running. They sign in on the app and
            record results from the venue.
          </p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 20 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
            <thead>
              <tr>{['Name', 'Sport', 'Username', 'Phone', 'Status', 'Signed in', ''].map((h) => (
                <th key={h} style={th}>{h}</th>))}</tr>
            </thead>
            <tbody>
              {scorers.map((s) => (
                <tr key={s.id}>
                  <td style={{ ...td, fontWeight: 600 }}>{s.full_name}</td>
                  <td style={td}>{s.sport_label}</td>
                  <td style={{ ...td, fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }}>{s.username}</td>
                  <td style={td}>{s.phone || '—'}</td>
                  <td style={td}>
                    <span style={pill(s.status === 'active' ? 'ok' : 'warn')}>
                      {s.status === 'active' ? 'Active' : 'Suspended'}
                    </span>
                  </td>
                  <td style={td}>
                    {s.signed_in
                      ? <span style={pill('ok')}>On a device</span>
                      : <span style={{ color: '#8b95a5' }}>—</span>}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {s.signed_in && (
                      <button style={miniGhost}
                        onClick={() => act(s.id, () => api.post(`/user/scorers/${s.id}/sign-out`),
                          'Sign this scorer out of every device? They can sign in again.')}>
                        Sign out
                      </button>
                    )}
                    <button style={miniGhost}
                      onClick={() => act(s.id, () => api.put(`/user/scorers/${s.id}`,
                        { status: s.status === 'active' ? 'suspended' : 'active' }))}>
                      {s.status === 'active' ? 'Suspend' : 'Reinstate'}
                    </button>
                    <button style={miniDanger}
                      onClick={() => act(s.id, () => api.delete(`/user/scorers/${s.id}`),
                        `Remove ${s.full_name}?`)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const wrap = { padding: 24, maxWidth: 1100, margin: '0 auto' }
const head = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }
const primaryBtn = { padding: '9px 20px', background: '#a4262c', color: '#fff', border: 0, borderRadius: 8, cursor: 'pointer', fontWeight: 700 }
const addBox = { marginTop: 18, padding: 18, background: '#fbfcfd', border: '1px solid #e4e9ef', borderRadius: 10 }
const empty = { padding: 26, marginTop: 20, background: '#f7f9fb', border: '1px dashed #d3dae2', borderRadius: 10 }
const lbl = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: '#5c6b7f', fontWeight: 600 }
const inp = { padding: '8px 10px', border: '1px solid #d9dee5', borderRadius: 7, fontSize: 13.5 }
const th = { textAlign: 'left', padding: '9px 12px', borderBottom: '2px solid #dde3ea', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.06em', color: '#5c6b7f' }
const td = { padding: '10px 12px', borderBottom: '1px solid #eef1f5', fontSize: 13.5 }
const miniGhost = { padding: '4px 10px', marginRight: 6, border: '1px solid #d9dee5', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 12 }
const miniDanger = { padding: '4px 10px', border: '1px solid #f0c9c6', background: '#fff', color: '#a4262c', borderRadius: 6, cursor: 'pointer', fontSize: 12 }
const credBox = { display: 'flex', gap: 20, marginTop: 8, fontFamily: 'ui-monospace, monospace', fontSize: 13 }
const pill = (tone) => ({
  padding: '2px 9px', borderRadius: 20, fontSize: 11.5, fontWeight: 700,
  background: tone === 'ok' ? '#e9f6ee' : '#fdf5e3',
  color: tone === 'ok' ? '#1c6b3a' : '#8a6412',
})
const banner = (tone) => ({
  marginTop: 16, padding: 13, borderRadius: 8, fontSize: 14,
  background: tone === 'ok' ? '#e9f6ee' : '#fdeceb',
  border: `1px solid ${tone === 'ok' ? '#bfe3cc' : '#f3c4c0'}`,
  color: tone === 'ok' ? '#1c6b3a' : '#a4262c',
})

export default MyScorers
